import useScrollWindow from '@/hooks/useScrollWindow';
import { FlowConnections } from './FlowConnections';
import { FingerPrintIcon } from '@heroicons/react/24/outline';
import { useStats, useSources, useAccessTypes } from './helpers';
import { motion } from 'framer-motion';
import useText from '@/hooks/useText';

export function HeroDiagram() {
  const { isMobile, isTablet } = useScrollWindow();
  const isDesktop = !isMobile && !isTablet;
  const stats = useStats();
  const sources = useSources();
  const accessTypes = useAccessTypes();
  const title1 = useText('about_disgenet.hero.title');
  const title2 = useText('about_disgenet.hero.title2');
  const subtitleStrong = useText('about_disgenet.hero.subtitle');
  const subtitleWeak = useText('about_disgenet.hero.subtitle_weak');
  const hint = useText('about_disgenet.hero.hint');
  const documentation_hint_prefix = useText('about_disgenet.hero.documentation_hint_prefix');
  const documentation_hint_link = useText('about_disgenet.hero.documentation_hint_link');
  const documentation_hint_suffix = useText('about_disgenet.hero.documentation_hint_suffix');
  const needs_title = useText('about_disgenet.use_cases.section_title');
  const needs_subtitle = useText('about_disgenet.use_cases.section_subtitle');
  const fit_prefix = useText('about_disgenet.use_cases.fit_prefix');
  const fit_highlight = useText('about_disgenet.use_cases.fit_highlight');
  const fit_suffix = useText('about_disgenet.use_cases.fit_suffix');

  return isDesktop ? (
    <div className="relative w-full mx-auto" style={{ maxWidth: '80vw' }}>
      {/* Title */}
      <div className="m-8" id="flow-1">
        <h1>
          <span id="flow-1-where">Where</span> {title2}
        </h1>
      </div>
      {/* Subtitle box */}
      <div className="flex justify-center">
        <div className="w-1/2 flow-2-box rounded-xl py-4 px-8 mx-8 mt-14 text-lg" id="flow-2">
          <strong>{subtitleStrong}</strong> {subtitleWeak}
        </div>
      </div>
      {/* Stats */}
      <div className="flex justify-center" id="flow-3">
        {stats.map(stat => (
          <div
            key={stat.id}
            className="stats-box rounded-xl mt-12 mx-4 p-4 text-md font-bold text-center text-blue"
            style={{
              background: 'rgb(190 0 128 / 8%)',
            }}
          >
            {stat.title}
          </div>
        ))}
      </div>
      {/* Sources */}
      <div className="flex justify-center" id="flow-4">
        {sources.map(src => (
          <div
            key={src.id}
            className="stats-box rounded-xl mt-32 mx-4 p-4 text-md font-bold text-center flex items-center justify-center text-blue"
            style={{
              background: 'rgb(25 25 139 / 12%)',
            }}
          >
            {src.title}
          </div>
        ))}
      </div>
      {/* Documentation hint */}
      <div className="flex justify-center">
        <div className="w-1/2 bg-gray-200 rounded-xl py-3 px-8 mx-8 mt-14 text-md text-blue" id="flow-5">
          <div className="flex items-center">
            <FingerPrintIcon className="w-8 h-8 mr-3" />
            {hint}
          </div>
          <small className="text-gray-500 flex items-end justify-end gap-1">
            <span>{documentation_hint_prefix}</span>
            <a href="/docs">{documentation_hint_link}</a>
            <span>{documentation_hint_suffix}</span>
          </small>
        </div>
      </div>
      {/* Needs */}
      <div className="flex flex-col items-center mt-24" id="flow-6">
        <span className="text-lg font-bold text-blue">{needs_title}</span>
        <span className="text-lg font-bold text-pink">{needs_subtitle}</span>
      </div>
      <div className="flex justify-center mt-12" id="flow-7">
        <div
          className="rounded-xl p-4 needs-box text-center text-2xl font-semibold text-blue"
          style={{
            backgroundImage: 'linear-gradient(180deg, rgb(25 25 139 / 12%) 0%, rgb(179 26 173 / 12%) 100%)',
          }}
        >
          {fit_prefix} <strong className="text-pink">{fit_highlight}</strong> {fit_suffix}
        </div>
      </div>
      {/* Access types */}
      <div className="flex justify-center mt-32" id="flow-8">
        {accessTypes.map(access => (
          <div key={access.id} className="flex flex-col items-center justify-center">
            <div className="rounded-xl py-4 px-2 needs-box text-lg font-semibold mx-12 flex justify-center text-blue items-center">
              <div className="flex items-center justify-center">
                {access.icon && <span className="mr-3">{access.icon}</span>}
              </div>
              <div className="flex flex-col text-lg">
                {access.title} <strong className="text-pink">{access.description}</strong>
              </div>
            </div>
            <small className="text-gray-500 text-sm mt-2">{access.hint}</small>
          </div>
        ))}
      </div>
      <FlowConnections />
    </div>
  ) : (
    <div className="m-4">
      {/* Title */}
      <motion.h1
        className="about-foundations__title"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <span id="flow-1-where">{title1}</span> {title2}
      </motion.h1>
      {/* Subtitle box */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="flow-2-box rounded-xl py-4 px-8 mt-4 text-lg">
          <strong>{subtitleStrong}</strong> {subtitleWeak}
        </div>
      </motion.div>
      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex flex-col items-center justify-center"
      >
        {stats.map(stat => (
          <div
            key={stat.id}
            className="stats-box rounded-xl mt-4 mx-4 p-4 text-md font-bold text-center text-blue"
          >
            {stat.title}
          </div>
        ))}
      </motion.div>
      {/* Sources */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="grid grid-cols-2 items-center gap-4 justify-items-center"
      >
        {sources.map(src => (
          <div
            key={src.id}
            className="src-box rounded-xl mt-4 p-4 text-md font-medium text-center text-white bg-pink"
          >
            {src.title}
          </div>
        ))}
      </motion.div>
      {/* Documentation hint */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex justify-center"
      >
        <div className=" bg-gray-200 rounded-xl py-3 px-4 mt-8 text-md text-blue" id="flow-5">
          <div className="flex items-center">
            <FingerPrintIcon className="w-16 h-16 mr-2" />
            {hint}
          </div>
        </div>
      </motion.div>
      <small className="text-gray-500 flex items-end justify-end gap-1 mt-2 md:mr-8">
        <span>{documentation_hint_prefix}</span>
        <a href="/docs">{documentation_hint_link}</a>
        <span>{documentation_hint_suffix}</span>
      </small>
      {/* Needs */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="mt-8 grid justify-center items-center text-center"
      >
        <span className="text-lg font-bold text-blue">Powerfull data, powerful results</span>
        <span className="text-lg font-bold text-pink">
          DISGENET provides the structured, explainable foundation
        </span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex justify-center mt-8"
      >
        <div className="rounded-xl p-4 needs-box text-center text-2xl font-semibold text-blue">
          {fit_prefix} <strong className="text-pink">{fit_highlight}</strong> {fit_suffix}
        </div>
      </motion.div>
      {/* Access types */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="grid access-grid"
      >
        {accessTypes.map(access => (
          <div key={access.id} className="flex flex-col items-center justify-center">
            <div className="rounded-xl py-4 px-2 needs-box text-lg font-semibold flex justify-evenly text-blue items-center mt-4">
              <div className="flex items-center justify-center">
                {access.icon && <span className="mr-3">{access.icon}</span>}
              </div>
              <div className="flex flex-col text-lg">
                {access.title} <strong className="text-pink">{access.description}</strong>
              </div>
            </div>
            <small className="text-gray-500 text-sm mt-2">{access.hint}</small>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
